import { generateDndNames, DndRace } from './dndNameGenerator';
import { generateFantasyNames, FantasyNameGeneratorOptions, SupportedLocale } from './fantasyNameGenerator';

export type NameCategory = 'dnd' | 'fantasy';
export type DnDRace = 'human' | 'elf' | 'dwarf' | 'orc' | 'halfling' | 'tiefling' | 'dragonborn' | 'gnome' | 'drow';
export type FantasyStyle = 'elf' | 'magic' | 'dragon';

export interface UnifiedNameGeneratorOptions {
    count?: number;
    category: NameCategory;
    subcategory: string;
    locale?: SupportedLocale;
}

export interface GenerationResult {
    names: string[];
    category: NameCategory;
    subcategory: string;
    timestamp: Date;
}

export interface CategoryConfiguration {
    dnd: {
        label: string;
        subcategories: { value: DnDRace; label: string }[];
    };
    fantasy: {
        label: string;
        subcategories: { value: FantasyStyle; label: string }[];
    };
}

export const CATEGORY_CONFIG: CategoryConfiguration = {
    dnd: {
        label: 'D&D Races',
        subcategories: [
            { value: 'human', label: 'Human' },
            { value: 'elf', label: 'Elf' },
            { value: 'dwarf', label: 'Dwarf' },
            { value: 'orc', label: 'Orc' },
            { value: 'halfling', label: 'Halfling' },
            { value: 'tiefling', label: 'Tiefling' },
            { value: 'dragonborn', label: 'Dragonborn' },
            { value: 'gnome', label: 'Gnome' },
            { value: 'drow', label: 'Drow' },
        ]
    },
    fantasy: {
        label: 'Fantasy Styles',
        subcategories: [
            { value: 'elf', label: 'Elf' },
            { value: 'magic', label: 'Magic' },
            { value: 'dragon', label: 'Dragon' },
        ]
    }
};

export class UnifiedNameGenerator {
    /**
     * Validates the generation request parameters
     */
    private static validateRequest(options: UnifiedNameGeneratorOptions): void {
        const { category, subcategory, count = 10 } = options;

        if (!category || !['dnd', 'fantasy'].includes(category)) {
            throw new Error('Invalid category. Must be "dnd" or "fantasy"');
        }

        if (!subcategory) {
            throw new Error('Subcategory is required');
        }

        if (count < 1 || count > 50) {
            throw new Error('Count must be between 1 and 50');
        }

        // Validate subcategory based on category
        const validSubcategories = CATEGORY_CONFIG[category].subcategories.map(s => s.value);
        if (!validSubcategories.includes(subcategory as any)) {
            throw new Error(`Invalid subcategory "${subcategory}" for category "${category}"`);
        }
    }

    /**
     * Generates names based on the unified interface
     */
    static async generateNames(options: UnifiedNameGeneratorOptions): Promise<GenerationResult> {
        this.validateRequest(options);

        const { category, subcategory, count = 10 } = options;
        let names: string[] = [];

        try {
            if (category === 'dnd') {
                names = generateDndNames({
                    count,
                    race: subcategory as DndRace
                });
            } else if (category === 'fantasy') {
                names = generateFantasyNames({
                    count,
                    style: subcategory as FantasyStyle,
                    locale: options.locale
                });
            }

            return {
                names,
                category,
                subcategory,
                timestamp: new Date()
            };
        } catch (error) {
            throw new Error(`Failed to generate names: ${error instanceof Error ? error.message : 'Unknown error'}`);
        }
    }

    /**
     * Gets available subcategories for a given category
     */
    static getSubcategories(category: NameCategory): { value: string; label: string }[] {
        return CATEGORY_CONFIG[category]?.subcategories || [];
    }

    /**
     * Gets the display label for a category
     */
    static getCategoryLabel(category: NameCategory): string {
        return CATEGORY_CONFIG[category]?.label || '';
    }
}